import { el } from './dom';

/** A short clip as a sprite sheet: frames left to right, then top to bottom. */
export interface ClipSheet {
  image: HTMLImageElement;
  frames: number;
  cols: number;
  fps: number;
}

/** Loads clip i of the brand's list, or null if it cannot. */
export type ClipLoader = (i: number) => Promise<ClipSheet | null>;

/**
 * The stadium big screen: after a touchdown it plays one of the brand's celebration clips once, in the middle
 * of the screen over a dark backdrop, in a frame that reads as the scoreboard screen rather than part of the
 * 3D game. Each touchdown gets the next clip in turn. Clips are sprite sheets drawn to a canvas, not videos:
 * they play the same on every phone (no autoplay rules) and under a strict content policy. Only the clip up
 * next is downloaded, so a phone fetches one sheet at a time. A tap ends a clip early.
 */
export class BigScreen {
  readonly root = el('div', 'bigscreen');
  private readonly frame = el('div', 'bigscreen-frame');
  private readonly canvas = el('canvas', 'bigscreen-video');
  private readonly ctx: CanvasRenderingContext2D | null;
  /** undefined: not asked for yet; null: loading or failed; else ready. */
  private clips: (ClipSheet | null | undefined)[] = [];
  private load: ClipLoader | null = null;
  private next = 0;
  private sheet: ClipSheet | null = null;
  private time = 0;
  private shown = -1;
  private on = false;

  constructor(label: string) {
    const tag = el('div', 'bigscreen-tag');
    tag.append(el('span', 'bigscreen-live', 'LIVE'), el('span', '', label));
    this.frame.append(this.canvas, tag);
    this.root.append(this.frame);
    this.root.dataset.testid = 'bigscreen';
    this.root.hidden = true;
    this.ctx = this.canvas.getContext('2d');
    this.root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.stop();
    });
  }

  /** The brand's clips: how many, how to load one, and which to show first. */
  setClips(count: number, load: ClipLoader, first = 0): void {
    this.clips = new Array<ClipSheet | null | undefined>(count).fill(undefined);
    this.load = count > 0 ? load : null;
    this.next = count > 0 ? ((first % count) + count) % count : 0;
    this.fetch(this.next);
  }

  private fetch(i: number): void {
    if (!this.load || this.clips[i] !== undefined) return;
    this.clips[i] = null;
    void this.load(i).then((c) => {
      this.clips[i] = c && c.image.naturalWidth > 0 && c.frames > 0 ? c : null;
    });
  }

  get playing(): boolean {
    return this.on;
  }

  /** Starts the next clip in turn (or any that has loaded). False when there is none to play. */
  play(): boolean {
    const n = this.clips.length;
    let pick = -1;
    for (let k = 0; k < n && pick < 0; k++) if (this.clips[(this.next + k) % n]) pick = (this.next + k) % n;
    if (pick < 0) return false;
    const s = this.clips[pick]!;
    this.next = (pick + 1) % n;
    this.fetch(this.next);
    this.sheet = s;
    const rows = Math.ceil(s.frames / s.cols);
    this.canvas.width = Math.round(s.image.naturalWidth / s.cols);
    this.canvas.height = Math.round(s.image.naturalHeight / rows);
    // The frame takes the clip's own shape: wide clips span the width, tall ones fill most of the height.
    this.frame.style.aspectRatio = `${this.canvas.width} / ${this.canvas.height}`;
    this.frame.classList.toggle('tall', this.canvas.height > this.canvas.width);
    this.time = 0;
    this.shown = -1;
    this.on = true;
    this.root.hidden = false;
    this.draw(0);
    return true;
  }

  stop(): void {
    this.on = false;
    this.root.hidden = true;
  }

  update(dt: number): void {
    const s = this.sheet;
    if (!this.on || !s) return;
    this.time += dt;
    const frame = Math.floor(this.time * s.fps);
    if (frame >= s.frames) {
      this.stop();
      return;
    }
    if (frame !== this.shown) this.draw(frame);
  }

  private draw(frame: number): void {
    const s = this.sheet;
    if (!s || !this.ctx) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    this.ctx.drawImage(s.image, (frame % s.cols) * w, Math.floor(frame / s.cols) * h, w, h, 0, 0, w, h);
    this.shown = frame;
  }
}
